import { mediaList, type ContentType, type MediaItem } from "@/mocks/media";

export type FilterState = {
  genres: string[];
  region: string[];
  year: string[];
  sort: "updated" | "heat" | "rating";
};

export const defaultFilterState: FilterState = {
  genres: [],
  region: [],
  year: [],
  sort: "updated",
};

export function filterByType(type: ContentType): MediaItem[] {
  return mediaList.filter((item) => item.type === type);
}

export function matchYear(item: MediaItem, years: string[]): boolean {
  if (years.length === 0) return true;
  return years.some((opt) => {
    if (opt === "更早") return item.year <= 2021;
    return item.year === Number(opt);
  });
}

export function sortItems(items: MediaItem[], sort: FilterState["sort"]): MediaItem[] {
  const list = [...items];
  if (sort === "heat") return list.sort((a, b) => b.heat - a.heat);
  if (sort === "rating") return list.sort((a, b) => b.rating - a.rating);
  return list.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
}

export function applyFilters(items: MediaItem[], filter: FilterState): MediaItem[] {
  const filtered = items.filter((item) => {
    const genreOk = filter.genres.length === 0 || item.genres.some((g) => filter.genres.includes(g));
    const regionOk = filter.region.length === 0 || filter.region.includes(item.region);
    const yearOk = matchYear(item, filter.year);
    return genreOk && regionOk && yearOk;
  });
  return sortItems(filtered, filter.sort);
}

export function searchMedia(keyword: string): MediaItem[] {
  const word = keyword.trim().toLowerCase();
  if (!word) return [];
  return mediaList.filter((item) => {
    const haystack = [item.title, item.enTitle, item.director, ...item.cast, ...item.genres, ...item.tags, item.region]
      .join(" ")
      .toLowerCase();
    return haystack.includes(word);
  });
}

export function getRelated(item: MediaItem, limit = 6): MediaItem[] {
  const sameType = mediaList.filter((m) => m.id !== item.id && m.type === item.type);
  const scored = sameType.map((m) => {
    const genreScore = m.genres.filter((g) => item.genres.includes(g)).length * 3;
    const regionScore = m.region === item.region ? 1 : 0;
    const tagScore = m.tags.filter((t) => item.tags.includes(t)).length * 2;
    return { item: m, score: genreScore + regionScore + tagScore };
  });
  return scored
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function getItemById(id: string): MediaItem | undefined {
  return mediaList.find((item) => item.id === id);
}

export function formatDate(date: string): string {
  return date.replace(/-/g, ".");
}

export function buildEpisodes(item: MediaItem): { group: string; items: number[] }[] {
  if (item.episodes <= 1) {
    return [{ group: "正片", items: [1] }];
  }
  const groups: { group: string; items: number[] }[] = [];
  const chunk = item.episodes > 30 ? 10 : 6;
  for (let start = 1; start <= item.episodes; start += chunk) {
    const end = Math.min(start + chunk - 1, item.episodes);
    groups.push({
      group: `第 ${start} - ${end} 集`,
      items: Array.from({ length: end - start + 1 }, (_, index) => start + index),
    });
  }
  return groups;
}